import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import * as api from '../../api';

const mergeUniqueByKey = (items, keyGetter) => {
  const map = new Map();

  items.forEach((item) => {
    const key = keyGetter(item);
    if (key) {
      map.set(key, item);
    }
  });

  return [...map.values()];
};

const buildTimeSlotType = ({ doctor, time_slot_type, searchFilters, medicalServicesId, currentjobId }) => {
  const hasShowPaidSlots = doctor?.data?.jobs?.some((job) => job.show_paid_slots === 1 && currentjobId == job.id) || false;

  let calculatedTimeSlotType = time_slot_type;

  if (medicalServicesId) {
    return 'paid';
  }

  if (searchFilters) {
    const types = [];

    if (searchFilters?.onlyAvailable) {
      types.push('free');
      if (hasShowPaidSlots) {
        types.push('paid');
      }
    }

    if (searchFilters?.byReferral) {
      types.push('referral');
      if (hasShowPaidSlots) {
        types.push('paid');
      }
    }

    if (searchFilters?.screening_40_plus) {
      types.push('screening_40_plus');
    }

    if (searchFilters?.defencer_benefit) {
      types.push('defencer_benefit');
    }

    if (types.length > 0) {
      calculatedTimeSlotType = types.join(',');
    } else if (
      searchFilters?.onlyAvailable !== undefined ||
      searchFilters?.byReferral !== undefined ||
      searchFilters?.screening_40_plus !== undefined ||
      searchFilters?.defencer_benefit !== undefined
    ) {
      calculatedTimeSlotType = hasShowPaidSlots ? 'free,referral,paid' : 'free,referral';
    } else {
      calculatedTimeSlotType = hasShowPaidSlots ? 'free,referral,paid' : time_slot_type;
    }
  } else if (hasShowPaidSlots) {
    calculatedTimeSlotType = `${time_slot_type},paid`;
  }

  return calculatedTimeSlotType;
};

export function useDoctorPageApi({ getDoctor, doctor, propDoctorId, medicalServicesId }) {
  const params = useParams();
  const doctorId = propDoctorId || params?.doctorId;
  
  useEffect(() => {
    if(doctorId && !doctor?.loading) {
      getDoctor(doctorId, medicalServicesId);
    }
  }, [doctorId, medicalServicesId]);
}

export function useDoctorScheduleApi({
  doctor,
  getDoctorsByDaysSchedule,
  doctorSchedule,
  doctorsByDaysSchedule,
  propDoctorId,
  selectedDate,
  selectedJobId,
  time_slot_type = 'free,referral',
  searchFilters,
  medicalServicesId,
  currentjobId,
}) {
  const [currentDoctorSchedule, setCurrentDoctorSchedule] = useState({ schedule: [] });
  const [loadingDatesState, setLoadingDatesState] = useState({});

  const scheduleByDateRef = useRef({});
  const isMountedRef = useRef(true);
  const loadedDatesRef = useRef(new Set());
  const loadingDatesRef = useRef(new Set());
  const cacheKeyRef = useRef('');

  const calculatedTimeSlotType = useMemo(() => {
    return buildTimeSlotType({ doctor, time_slot_type, searchFilters, medicalServicesId, currentjobId });
  }, [doctor, time_slot_type, searchFilters, medicalServicesId, currentjobId]);

  const doctorId = doctor?.data?.id;

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!doctorId) {
      setCurrentDoctorSchedule({ schedule: [] });
      return;
    }

    const newCacheKey = [
      doctorId,
      propDoctorId || '',
      calculatedTimeSlotType,
      medicalServicesId || '',
      currentjobId || '',
    ].join('|');

    if (cacheKeyRef.current !== newCacheKey) {
      cacheKeyRef.current = newCacheKey;
      scheduleByDateRef.current = {};
      loadedDatesRef.current = new Set();
      loadingDatesRef.current = new Set();
      setCurrentDoctorSchedule({ schedule: [] });
      setLoadingDatesState({});
    }
  }, [doctorId, propDoctorId, calculatedTimeSlotType, medicalServicesId, currentjobId]);

  useEffect(() => {
    if (!doctor?.data) {
      return;
    }

    if (propDoctorId && Number(propDoctorId) !== Number(doctor?.data?.id)) {
      return;
    }

    if (doctorsByDaysSchedule?.data.length === 0) {
      getDoctorsByDaysSchedule({
        date_from: doctorSchedule.initialDateFrom,
        date_to: doctorSchedule.initialDateTo,
        ids: doctor.data.id,
        time_slot_type: calculatedTimeSlotType,
      });
    }
  }, [
    getDoctorsByDaysSchedule,
    doctor?.data,
    doctorSchedule?.initialDateFrom,
    doctorSchedule?.initialDateTo,
    doctorsByDaysSchedule?.data.length,
    propDoctorId,
    calculatedTimeSlotType,
  ]);

  useEffect(() => {
    if (!doctorId || !selectedDate) {
      return;
    }

    if (propDoctorId && Number(propDoctorId) !== Number(doctorId)) {
      return;
    }

    if (loadedDatesRef.current.has(selectedDate) || loadingDatesRef.current.has(selectedDate)) {
      return;
    }

    const daySchedules = doctorsByDaysSchedule?.data?.[0]?.schedule || [];
    const selectedDaySchedules = daySchedules.filter((day) => {
      const isSameDate = day?.time_from?.slice(0, 10) === selectedDate;
      if (!isSameDate) {
        return false;
      }

      if (!selectedJobId) {
        return true;
      }

      return Number(day.job_id) === Number(selectedJobId);
    });

    const isAbsentDay = selectedDaySchedules.length > 0 && selectedDaySchedules.every((day) => day.status !== 'FREE');

    if (isAbsentDay) {
      scheduleByDateRef.current[selectedDate] = [];
      loadedDatesRef.current.add(selectedDate);
      return;
    }
    const requestCacheKey = cacheKeyRef.current;

    const loadDay = async () => {
      loadingDatesRef.current.add(selectedDate);
      setLoadingDatesState((prev) => ({
        ...prev,
        [selectedDate]: true,
      }));

      try {
        const scheduleResponse = await api.getDoctorSchedule(
          {
            ids: doctorId,
            date_from: selectedDate,
            date_to: selectedDate,
            time_slot_type: calculatedTimeSlotType,
          },
          { disableLoader: true },
        );

        if (cacheKeyRef.current !== requestCacheKey) {
          return;
        }

        if (!isMountedRef.current) {
          return;
        }

        const daySlots = scheduleResponse?.data?.[0]?.schedule || [];

        scheduleByDateRef.current[selectedDate] = daySlots;
        loadedDatesRef.current.add(selectedDate);

        const mergedSchedule = mergeUniqueByKey(
          Object.values(scheduleByDateRef.current).flat(),
          (item) => `${item?.id || ''}_${item?.datetime || item?.time_from || ''}_${item?.job_id || ''}`,
        );

        setCurrentDoctorSchedule({
          ...(scheduleResponse?.data?.[0] || { id: doctorId }),
          schedule: mergedSchedule,
        });
      } catch (error) {
        // no-op: existing UI handles empty state
      } finally {
        loadingDatesRef.current.delete(selectedDate);

        if (isMountedRef.current) {
          setLoadingDatesState((prev) => {
            const next = { ...prev };
            delete next[selectedDate];
            return next;
          });
        }
      }
    };

    loadDay();
  }, [
    doctorId,
    propDoctorId,
    selectedDate,
    calculatedTimeSlotType,
    doctorsByDaysSchedule?.data,
    selectedJobId,
  ]);

  return {
    currentDoctorSchedule,
    isSelectedDateLoading: Boolean(selectedDate && loadingDatesState[selectedDate]),
  };
}
